
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ValidationProvider, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import { JobComplianceServiceLookupData } from '@/models/JobComplianceTypes';
import store from '@/store';

extend('required', {
  ...required,
  message: 'This field is required',
});

@Component({
  components: {
    ValidationProvider,
  },
})
export default class LookupServices extends Vue {
  @Prop({ type: Array, required: true }) public serviceData: JobComplianceServiceLookupData[];
  @Prop({ type: Array, required: true }) public serviceDataChosen: string[];
  @Prop({ type: String, required: true }) public name: string;
  @Prop({ type: String, required: false }) public serviceCode: string;

  public loading = false;
  public serviceValue = '';
  public selectItems: string[] = [];
  public showOptions = false;
  public arrowIdx = -1;
  private serviceChosen: JobComplianceServiceLookupData = {
    code: '',
    description: '',
  };
  @Watch('serviceCode', { immediate: true })
  private watchPropServiceCode() {
    this.serviceValue = this.serviceCode;
  }

  @Watch('serviceChosen', { deep: true })
  private watchServiceChosen() {
    this.$emit('changed', this.serviceChosen.code, this.name.slice(-1));
  }

  private applyValueFromParent(item): void {
    this.serviceChosen = item;
    this.showOptions = false;
    this.serviceValue = item.code;
    this.$forceUpdate();
  }

  public handleInput(evt): void {
    const filteredAddresses = this.serviceData
      .filter(x => this.serviceDataChosen.indexOf(x.code) === -1)
      .filter(x => `${x.code} - ${x.description}`.toUpperCase().indexOf(String(evt.target.value).toUpperCase()) >= 0)
      .sort((a, b) => {
        const firstVal = `${a.code} - ${a.description}`.toUpperCase();
        const secondVal = `${b.code} - ${b.description}`.toUpperCase();
        const inputVal = evt.target.value.toUpperCase();
        const firstValToSortString = firstVal.slice(0, inputVal.length);
        const secondValToSortString = secondVal.slice(0, inputVal.length);
        if (firstValToSortString === inputVal) {
          return -1;
        }
        if (secondValToSortString === inputVal) {
          return 1;
        }
        return firstVal.localeCompare(secondVal);
      });
    this.selectItems = [
      ...filteredAddresses.map(
        (service: JobComplianceServiceLookupData) =>
          `${service.code}${service.description ? ` - ${service.description}` : ''}`,
      ),
    ];
    this.$emit('changed', evt.target.value.toUpperCase(), this.name.slice(-1));
  }

  public inputKeyPressed(evt: KeyboardEvent): void {
    // Open dropdown on Focus
    if (!this.showOptions) {
      this.showOptions = true;
      this.arrowIdx = -1;
    }
    // Close dropdown when "Escape" pressed
    if (evt.code === 'Escape') {
      this.showOptions = false;
      this.arrowIdx = -1;
    }
    // ArrowDown / ArrowUp pressed
    if (evt.code === 'ArrowDown' && this.arrowIdx < this.selectItems.length - 1) {
      this.arrowIdx += 1;
      this.fixScrolling();
    }
    if (evt.code === 'ArrowUp' && this.arrowIdx >= 1) {
      this.arrowIdx -= 1;
      this.fixScrolling();
    }
    // Enter pressed
    if (evt.code === 'Enter' && this.arrowIdx >= 0) {
      evt.preventDefault();
      const itemCode = this.selectItems[this.arrowIdx].split(' - ')[0];

      const chosenService = this.serviceData.find((x: JobComplianceServiceLookupData) => x.code === itemCode);
      if (chosenService) {
        this.serviceChosen = chosenService;
        this.showOptions = false;
        this.serviceValue = itemCode;
        this.arrowIdx = -1;
      }
    }
  }

  private fixScrolling() {
    const options = this.$refs.options as HTMLFormElement;
    const scrollContainer = this.$refs.scrollContainer as HTMLFormElement;
    if (options && scrollContainer) {
      const liH = options[this.arrowIdx].clientHeight;
      scrollContainer.scrollTop = liH * this.arrowIdx;
    }
  }

  public inputFocused(): void {
    this.showOptions = true;
    // filter by input value when open
    if (this.serviceValue.length) {
      this.handleInput({
        target: {
          value: this.serviceValue,
        },
      });
    } else {
      this.selectItems = this.serviceData
        .filter(x => this.serviceDataChosen.indexOf(x.code) === -1)
        .filter(x => `${x.code} - ${x.description}`.toUpperCase().indexOf(this.serviceValue.toUpperCase()) >= 0)
        .map((service: JobComplianceServiceLookupData) => `${service.code} - ${service.description}`);
    }
  }

  public inputBlured(): void {
    setTimeout(() => {
      this.showOptions = false;
    }, 200);
  }

  public handleItemClick(item: string): void {
    const itemCode = item.split(' - ')[0];

    const chosenService = this.serviceData.find((x: JobComplianceServiceLookupData) => x.code === itemCode);

    if (chosenService) {
      this.serviceChosen = chosenService;
      this.showOptions = false;
      this.serviceValue = itemCode;
    }
  }

  public clickedOutside(): void {
    this.showOptions = false;
  }

  public oneOfExtendValidationRule = extend('oneOf', value => {
    return store.getters['moduleStaticData/getServiceCodes'].map(x => x.code).includes(value.toUpperCase())
      ? true
      : 'Does not match';
  });
}
